const TAU = Math.PI * 2;

const DEFAULT_WALKABLE_MAX_SLOPE_DEG = 32;
const DEFAULT_CLIMBABLE_MAX_SLOPE_DEG = 52;

function ringPoints(prefix, radius, count = 16) {
  return Array.from({ length: count }, (_, index) => {
    const angle = (index / count) * TAU;
    return {
      id: `${prefix}_${String(index + 1).padStart(2, "0")}`,
      x: Math.cos(angle) * radius,
      z: Math.sin(angle) * radius,
    };
  });
}

function createDefaultBoundary(size) {
  return {
    outer: ringPoints("outer", size * 0.47),
    holes: [ringPoints("hole_01", size * 0.15, 12)],
  };
}

function readPoint(pointOrX, zOrPolygon, maybePolygon) {
  if (typeof pointOrX === "object" && pointOrX !== null) {
    return { point: pointOrX, polygon: zOrPolygon };
  }
  return { point: { x: pointOrX, z: zOrPolygon }, polygon: maybePolygon };
}

function pointOnSegment(point, start, end) {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const cross = (point.x - start.x) * dz - (point.z - start.z) * dx;
  if (Math.abs(cross) > 1e-7) return false;
  const dot = (point.x - start.x) * dx + (point.z - start.z) * dz;
  return dot >= -1e-7 && dot <= dx * dx + dz * dz + 1e-7;
}

/** Returns true when a point is inside a ring, including its boundary. */
export function pointInPolygon(pointOrX, zOrPolygon, maybePolygon) {
  const { point, polygon } = readPoint(pointOrX, zOrPolygon, maybePolygon);
  if (!Array.isArray(polygon) || polygon.length < 3 || !Number.isFinite(point?.x) || !Number.isFinite(point?.z)) return false;
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const currentPoint = polygon[index];
    const previousPoint = polygon[previous];
    if (pointOnSegment(point, previousPoint, currentPoint)) return true;
    const intersects = ((currentPoint.z > point.z) !== (previousPoint.z > point.z))
      && point.x < ((previousPoint.x - currentPoint.x) * (point.z - currentPoint.z)) / (previousPoint.z - currentPoint.z) + currentPoint.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Returns true when a point is in the outer ring and not in any hole. */
export function pointInBoundary(pointOrX, zOrBoundary, maybeBoundary) {
  const { point, polygon: boundary } = readPoint(pointOrX, zOrBoundary, maybeBoundary);
  if (!boundary?.outer || !pointInPolygon(point, boundary.outer)) return false;
  return !(boundary.holes ?? []).some((hole) => pointInPolygon(point, hole));
}

function distanceToSegment(point, start, end) {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const lengthSquared = dx * dx + dz * dz;
  if (lengthSquared <= Number.EPSILON) return Math.hypot(point.x - start.x, point.z - start.z);
  const t = clamp(((point.x - start.x) * dx + (point.z - start.z) * dz) / lengthSquared, 0, 1);
  return Math.hypot(point.x - (start.x + dx * t), point.z - (start.z + dz * t));
}

function boundaryClearance(boundary, point) {
  let closest = Number.POSITIVE_INFINITY;
  for (const ring of [boundary?.outer, ...(boundary?.holes ?? [])]) {
    if (!Array.isArray(ring) || ring.length < 2) continue;
    for (let index = 0; index < ring.length; index++) {
      closest = Math.min(closest, distanceToSegment(point, ring[index], ring[(index + 1) % ring.length]));
    }
  }
  return closest;
}

export const TERRAIN_PRESETS = {
  alpine: {
    label: "Alpine basin",
    description: "A ring of high ground draining into a sheltered lake and valley.",
    seed: 41721,
    roughness: 0.72,
    erosion: 7,
    waterLevel: 6,
    features: [
      { id: "mountain_1", type: "mountain", x: -210, z: -170, radius: 250, height: 118, falloff: 1.8 },
      { id: "ridge_1", type: "ridge", x: 205, z: -90, radius: 115, length: 460, height: 76, angle: 0.42, falloff: 1.7 },
      { id: "lake_1", type: "lake", x: 35, z: 80, radius: 126, depth: 24, rim: 7 },
      { id: "valley_1", type: "valley", x: -35, z: 205, radius: 82, length: 520, depth: 35, angle: 0.08, falloff: 1.6 },
    ],
  },
  highlands: {
    label: "Broken highlands",
    description: "Long ridges, traversable saddles, and exposed lookout ground.",
    seed: 9834,
    roughness: 0.84,
    erosion: 5,
    waterLevel: -12,
    features: [
      { id: "ridge_1", type: "ridge", x: -125, z: 0, radius: 135, length: 710, height: 96, angle: -0.55, falloff: 1.55 },
      { id: "ridge_2", type: "ridge", x: 205, z: 80, radius: 105, length: 520, height: 72, angle: 0.92, falloff: 1.65 },
      { id: "pass_1", type: "pass", x: 0, z: 15, radius: 75, length: 300, depth: 45, angle: -0.55, falloff: 1.7 },
    ],
  },
  riverlands: {
    label: "Riverlands",
    description: "Low rolling country organized by a broad river corridor and floodplain.",
    seed: 62019,
    roughness: 0.43,
    erosion: 9,
    waterLevel: 2,
    features: [
      { id: "mountain_1", type: "mountain", x: -295, z: -245, radius: 245, height: 72, falloff: 2.1 },
      { id: "river_1", type: "river", x: 0, z: 5, radius: 42, length: 940, depth: 18, angle: 0.18, falloff: 1.4 },
      { id: "valley_1", type: "valley", x: 160, z: -70, radius: 105, length: 650, depth: 19, angle: 0.18, falloff: 1.8 },
    ],
  },
};

export function createTerrainState({ size = 1000, resolution = 129, preset = "alpine" } = {}) {
  const source = TERRAIN_PRESETS[preset] ?? TERRAIN_PRESETS.alpine;
  const normalizedSize = Math.max(300, size);
  return {
    version: 2,
    size: normalizedSize,
    resolution,
    baseHeight: 12,
    seed: source.seed,
    hydrologySeed: (source.seed ^ 0x51e8c7d) >>> 0,
    poiSeed: (source.seed ^ 0x2f6e2b1) >>> 0,
    roughness: source.roughness,
    erosion: source.erosion,
    waterLevel: source.waterLevel,
    boundary: createDefaultBoundary(normalizedSize),
    manualHeightControls: [],
    traversal: {
      walkableMaxSlopeDeg: DEFAULT_WALKABLE_MAX_SLOPE_DEG,
      climbableMaxSlopeDeg: DEFAULT_CLIMBABLE_MAX_SLOPE_DEG,
    },
    constraints: { regions: [] },
    features: source.features.map((feature) => ({ ...feature })),
    poiRules: [
      { id: "settlements", type: "settlement", label: "Settlement", count: 5, maxSlope: 11, minSpacing: 115, radius: 30, padRadius: 38, blend: 14 },
      { id: "lookouts", type: "lookout", label: "Lookout", count: 3, maxSlope: 24, minSpacing: 145, radius: 18, padRadius: 24, blend: 10 },
      { id: "water_access", type: "lakeside", label: "Water access", count: 3, maxSlope: 14, minSpacing: 90, radius: 24, padRadius: 32, blend: 12 },
    ],
    pois: [],
  };
}

export function cloneTerrainState(state) {
  return typeof structuredClone === "function" ? structuredClone(state) : JSON.parse(JSON.stringify(state));
}

function mulberry32(seed) {
  let value = seed >>> 0;
  return () => {
    value |= 0;
    value = (value + 0x6d2b79f5) | 0;
    let t = Math.imul(value ^ (value >>> 15), 1 | value);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash2(x, z, seed) {
  let h = Math.imul(x | 0, 374761393) + Math.imul(z | 0, 668265263) + Math.imul(seed | 0, 1442695041);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

const smooth = (t) => t * t * (3 - 2 * t);
const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function valueNoise(x, z, seed) {
  const x0 = Math.floor(x), z0 = Math.floor(z);
  const tx = smooth(x - x0), tz = smooth(z - z0);
  const a = lerp(hash2(x0, z0, seed), hash2(x0 + 1, z0, seed), tx);
  const b = lerp(hash2(x0, z0 + 1, seed), hash2(x0 + 1, z0 + 1, seed), tx);
  return lerp(a, b, tz) * 2 - 1;
}

function fbm(x, z, seed) {
  let value = 0, amplitude = 0.58, frequency = 1, total = 0;
  for (let octave = 0; octave < 5; octave++) {
    value += valueNoise(x * frequency, z * frequency, seed + octave * 1013) * amplitude;
    total += amplitude;
    amplitude *= 0.5;
    frequency *= 2.03;
  }
  return value / total;
}

function localFrame(feature, x, z) {
  const dx = x - feature.x, dz = z - feature.z;
  const angle = feature.angle ?? 0;
  const cos = Math.cos(angle), sin = Math.sin(angle);
  return { along: dx * cos + dz * sin, across: -dx * sin + dz * cos };
}

function riverMeanderPhase(seed) {
  return ((seed >>> 0) % 4096) * (TAU / 4096);
}

function featureDelta(feature, x, z, base, hydrologySeed = 0) {
  const radius = Math.max(feature.radius ?? 100, 1);
  const falloff = feature.falloff ?? 1.8;
  if (feature.type === "mountain") {
    const distance = Math.hypot(x - feature.x, z - feature.z) / radius;
    if (distance >= 1.35) return 0;
    const mass = Math.exp(-Math.pow(distance * 1.72, falloff));
    const ridges = 0.82 + Math.abs(valueNoise(x / 52, z / 52, (feature.id.length * 1907) | 0)) * 0.24;
    return (feature.height ?? 70) * mass * ridges;
  }

  if (["ridge", "valley", "pass", "river"].includes(feature.type)) {
    const { along, across } = localFrame(feature, x, z);
    const length = Math.max(feature.length ?? radius * 4, radius * 2);
    const half = length / 2;
    const taperedAlong = Math.abs(along) <= half ? 0 : (Math.abs(along) - half) / radius;
    const meander = feature.type === "river" ? Math.sin(along / 82 + feature.x * 0.01 + riverMeanderPhase(hydrologySeed)) * radius * 0.42 : 0;
    const distance = Math.hypot((across - meander) / radius, taperedAlong);
    if (distance >= 1.25) return 0;
    const profile = Math.exp(-Math.pow(distance * 1.95, falloff));
    if (feature.type === "ridge") return (feature.height ?? 55) * profile;
    return -(feature.depth ?? (feature.type === "pass" ? 36 : 24)) * profile;
  }

  if (feature.type === "lake") {
    const distance = Math.hypot(x - feature.x, z - feature.z) / radius;
    const basin = distance < 1 ? -(feature.depth ?? 18) * Math.pow(1 - distance * distance, 1.7) : 0;
    const rim = (feature.rim ?? 5) * Math.exp(-Math.pow((distance - 1.02) / 0.17, 2));
    return basin + rim;
  }

  if (feature.type === "plateau") {
    const distance = Math.hypot(x - feature.x, z - feature.z) / radius;
    if (distance >= 1.2) return 0;
    const blend = 1 - smooth(clamp((distance - 0.68) / 0.52, 0, 1));
    return ((feature.height ?? 48) - base) * blend;
  }
  return 0;
}

function erodeHeightfield(heights, resolution, cellSize, iterations) {
  const current = heights;
  const talus = Math.tan((31 * Math.PI) / 180) * cellSize;
  const delta = new Float32Array(current.length);
  for (let iteration = 0; iteration < iterations; iteration++) {
    delta.fill(0);
    for (let z = 1; z < resolution - 1; z++) {
      for (let x = 1; x < resolution - 1; x++) {
        const index = z * resolution + x;
        const neighbors = [index - 1, index + 1, index - resolution, index + resolution];
        let steepest = index, difference = 0;
        for (const neighbor of neighbors) {
          const candidate = current[index] - current[neighbor];
          if (candidate > difference) { difference = candidate; steepest = neighbor; }
        }
        if (steepest !== index && difference > talus) {
          const moved = (difference - talus) * 0.18;
          delta[index] -= moved;
          delta[steepest] += moved;
        }
      }
    }
    for (let i = 0; i < current.length; i++) current[i] += delta[i];
  }
  return current;
}

function legacySquareBoundary(size) {
  const half = size / 2;
  return {
    outer: [
      { id: "legacy_01", x: -half, z: -half },
      { id: "legacy_02", x: half, z: -half },
      { id: "legacy_03", x: half, z: half },
      { id: "legacy_04", x: -half, z: half },
    ],
    holes: [],
  };
}

function resolvedBoundary(state) {
  return state.boundary?.outer?.length >= 3 ? state.boundary : legacySquareBoundary(state.size);
}

function manualHeightControls(state) {
  return state.manualHeightControls ?? state.heightControls ?? [];
}

function applyManualHeightControls(heights, state, resolution, cellSize, half) {
  for (const control of manualHeightControls(state)) {
    if (!Number.isFinite(control?.x) || !Number.isFinite(control?.z)) continue;
    const radius = Math.max(control.radius ?? control.padRadius ?? 30, 1);
    const blend = Math.max(control.blend ?? radius * 0.35, 0);
    const influenceRadius = radius + blend;
    const minColumn = clamp(Math.floor((control.x - influenceRadius + half) / cellSize), 0, resolution - 1);
    const maxColumn = clamp(Math.ceil((control.x + influenceRadius + half) / cellSize), 0, resolution - 1);
    const minRow = clamp(Math.floor((control.z - influenceRadius + half) / cellSize), 0, resolution - 1);
    const maxRow = clamp(Math.ceil((control.z + influenceRadius + half) / cellSize), 0, resolution - 1);
    for (let row = minRow; row <= maxRow; row++) {
      const z = -half + row * cellSize;
      for (let column = minColumn; column <= maxColumn; column++) {
        const x = -half + column * cellSize;
        const distance = Math.hypot(x - control.x, z - control.z);
        if (distance > influenceRadius) continue;
        const blendAmount = distance <= radius ? 1 : 1 - smooth((distance - radius) / Math.max(blend, Number.EPSILON));
        const index = row * resolution + column;
        if (Number.isFinite(control.height)) heights[index] = lerp(heights[index], control.height, blendAmount);
        else if (Number.isFinite(control.delta)) heights[index] += control.delta * blendAmount;
      }
    }
  }
}

function applyPoiHeightPads(heights, pois, resolution, cellSize, half) {
  const ordered = [...(pois ?? [])].sort((left, right) => String(left.id).localeCompare(String(right.id)));
  for (const poi of ordered) {
    if (!Number.isFinite(poi?.x) || !Number.isFinite(poi?.z) || !Number.isFinite(poi?.y)) continue;
    const radius = Math.max(poi.padRadius ?? poi.radius ?? 0, 0);
    if (radius <= 0) continue;
    const blend = Math.max(poi.blend ?? 0, 0);
    const influenceRadius = radius + blend;
    const minColumn = clamp(Math.floor((poi.x - influenceRadius + half) / cellSize), 0, resolution - 1);
    const maxColumn = clamp(Math.ceil((poi.x + influenceRadius + half) / cellSize), 0, resolution - 1);
    const minRow = clamp(Math.floor((poi.z - influenceRadius + half) / cellSize), 0, resolution - 1);
    const maxRow = clamp(Math.ceil((poi.z + influenceRadius + half) / cellSize), 0, resolution - 1);
    for (let row = minRow; row <= maxRow; row++) {
      const z = -half + row * cellSize;
      for (let column = minColumn; column <= maxColumn; column++) {
        const x = -half + column * cellSize;
        const distance = Math.hypot(x - poi.x, z - poi.z);
        if (distance > influenceRadius) continue;
        const amount = distance <= radius ? 1 : 1 - smooth((distance - radius) / Math.max(blend, Number.EPSILON));
        const index = row * resolution + column;
        heights[index] = lerp(heights[index], poi.y, amount);
      }
    }
  }
}

export function generateTerrain(state, { poiPads = state.pois ?? [] } = {}) {
  const resolution = state.resolution;
  const count = resolution * resolution;
  const heights = new Float32Array(count);
  const cellSize = state.size / (resolution - 1);
  const half = state.size / 2;
  const boundary = resolvedBoundary(state);
  const inside = new Uint8Array(count);
  let min = Number.POSITIVE_INFINITY, max = Number.NEGATIVE_INFINITY;

  for (let row = 0; row < resolution; row++) {
    const z = -half + row * cellSize;
    for (let column = 0; column < resolution; column++) {
      const x = -half + column * cellSize;
      const broad = fbm(x / 310, z / 310, state.seed) * state.roughness * 22;
      const detail = fbm(x / 92, z / 92, state.seed + 9001) * state.roughness * 7;
      let height = state.baseHeight + broad + detail;
      for (const feature of state.features) height += featureDelta(feature, x, z, height, state.hydrologySeed ?? state.seed);
      heights[row * resolution + column] = height;
      inside[row * resolution + column] = pointInBoundary(x, z, boundary) ? 1 : 0;
    }
  }

  erodeHeightfield(heights, resolution, cellSize, clamp(Math.round(state.erosion), 0, 12));
  applyPoiHeightPads(heights, poiPads, resolution, cellSize, half);
  applyManualHeightControls(heights, state, resolution, cellSize, half);
  for (let index = 0; index < heights.length; index++) {
    if (!inside[index]) continue;
    min = Math.min(min, heights[index]);
    max = Math.max(max, heights[index]);
  }
  if (!Number.isFinite(min)) { min = 0; max = 0; }
  return { heights, inside, boundary, resolution, size: state.size, cellSize, min, max };
}

export function sampleTerrain(terrain, x, z) {
  const half = terrain.size / 2;
  const gx = clamp((x + half) / terrain.cellSize, 0, terrain.resolution - 1);
  const gz = clamp((z + half) / terrain.cellSize, 0, terrain.resolution - 1);
  const x0 = Math.floor(gx), z0 = Math.floor(gz), x1 = Math.min(x0 + 1, terrain.resolution - 1), z1 = Math.min(z0 + 1, terrain.resolution - 1);
  const tx = gx - x0, tz = gz - z0;
  const a = lerp(terrain.heights[z0 * terrain.resolution + x0], terrain.heights[z0 * terrain.resolution + x1], tx);
  const b = lerp(terrain.heights[z1 * terrain.resolution + x0], terrain.heights[z1 * terrain.resolution + x1], tx);
  return lerp(a, b, tz);
}

export function terrainSlope(terrain, x, z) {
  const step = terrain.cellSize;
  const dx = sampleTerrain(terrain, x + step, z) - sampleTerrain(terrain, x - step, z);
  const dz = sampleTerrain(terrain, x, z + step) - sampleTerrain(terrain, x, z - step);
  return Math.atan(Math.hypot(dx, dz) / (step * 2)) * 180 / Math.PI;
}

function terrainMaskInside(terrain, x, z) {
  if (!terrain.inside) return true;
  const half = terrain.size / 2;
  const column = clamp(Math.round((x + half) / terrain.cellSize), 0, terrain.resolution - 1);
  const row = clamp(Math.round((z + half) / terrain.cellSize), 0, terrain.resolution - 1);
  return terrain.inside[row * terrain.resolution + column] === 1;
}

function pointIsPlayable(state, terrain, x, z) {
  const boundary = terrain?.boundary ?? resolvedBoundary(state);
  return pointInBoundary(x, z, boundary) && terrainMaskInside(terrain, x, z);
}

function isWaterAt(state, terrain, x, z) {
  if (sampleTerrain(terrain, x, z) <= state.waterLevel) return true;
  return (state.features ?? []).some((feature) => feature.type === "lake"
    && Math.hypot(x - feature.x, z - feature.z) <= feature.radius
    && sampleTerrain(terrain, x, z) <= Math.max(state.waterLevel, state.baseHeight));
}

function distanceToWater(state, x, z) {
  let best = Number.POSITIVE_INFINITY;
  for (const feature of state.features) {
    if (feature.type === "lake") best = Math.min(best, Math.abs(Math.hypot(x - feature.x, z - feature.z) - feature.radius));
    if (feature.type === "river") {
      const { along, across } = localFrame(feature, x, z);
      const meander = Math.sin(along / 82 + feature.x * 0.01 + riverMeanderPhase(state.hydrologySeed ?? state.seed)) * (feature.radius ?? 35) * 0.42;
      if (Math.abs(along) < (feature.length ?? 600) / 2) best = Math.min(best, Math.abs(across - meander) - (feature.radius ?? 35));
    }
  }
  return Math.abs(best);
}

export function generatePois(state, terrain) {
  const boundary = terrain?.boundary ?? resolvedBoundary(state);
  const manual = (state.pois ?? []).filter((poi) => poi.manual || poi.pinned);
  const generated = [];
  const usedIds = new Set(manual.map((poi) => poi.id));
  const random = mulberry32(((state.poiSeed ?? state.seed) ^ 0x91e10da5) >>> 0);
  const half = state.size / 2;
  const candidates = [];
  for (let i = 0; i < 1000; i++) {
    const x = lerp(-half * 0.9, half * 0.9, random());
    const z = lerp(-half * 0.9, half * 0.9, random());
    if (!pointInBoundary(x, z, boundary) || !terrainMaskInside(terrain, x, z)) continue;
    const y = sampleTerrain(terrain, x, z);
    candidates.push({ x, z, y, slope: terrainSlope(terrain, x, z), water: distanceToWater(state, x, z), jitter: random() });
  }

  for (const rule of state.poiRules ?? []) {
    const radius = Math.max(rule.radius ?? 24, 1);
    const padRadius = Math.max(rule.padRadius ?? radius, radius);
    const blend = Math.max(rule.blend ?? Math.round(padRadius * 0.35), 0);
    const requiredClearance = padRadius + blend;
    const ranked = candidates
      .filter((candidate) => candidate.slope <= rule.maxSlope
        && boundaryClearance(boundary, candidate) >= requiredClearance
        && pointIsPlayable(state, terrain, candidate.x, candidate.z))
      .map((candidate) => {
        const normalizedHeight = (candidate.y - terrain.min) / Math.max(terrain.max - terrain.min, 1);
        let score = candidate.jitter * 0.2;
        if (rule.type === "lookout") score += normalizedHeight * 1.5 - Math.abs(candidate.slope - 12) / 55;
        else if (rule.type === "lakeside") score += Number.isFinite(candidate.water) ? 1.4 - Math.min(Math.abs(candidate.water - 28) / 100, 1.4) : -10;
        else score += (1 - candidate.slope / Math.max(rule.maxSlope, 1)) + (Number.isFinite(candidate.water) ? Math.max(0, 1 - candidate.water / 260) * 0.38 : 0);
        return { ...candidate, score };
      })
      .sort((a, b) => b.score - a.score);

    const manualForRule = manual.filter((poi) => poi.ruleId === rule.id).length;
    let placed = manualForRule;
    let idIndex = 1;
    if (placed >= rule.count) continue;
    for (const candidate of ranked) {
      const spacing = rule.minSpacing ?? 100;
      const conflicts = [...manual, ...generated].some((poi) => Math.hypot(poi.x - candidate.x, poi.z - candidate.z) < Math.max(spacing, poi.minSpacing ?? 0, padRadius + (poi.padRadius ?? poi.radius ?? 0)));
      if (conflicts) continue;
      while (usedIds.has(`${rule.id}_${idIndex}`)) idIndex++;
      const id = `${rule.id}_${idIndex++}`;
      usedIds.add(id);
      generated.push({
        id,
        type: rule.type,
        label: `${rule.label} ${placed + 1}`,
        x: candidate.x,
        y: candidate.y,
        z: candidate.z,
        slope: candidate.slope,
        manual: false,
        ruleId: rule.id,
        radius,
        padRadius,
        blend,
        minSpacing: spacing,
      });
      placed++;
      if (placed >= rule.count) break;
    }
  }
  return [...manual.map((poi) => ({
    ...poi,
    manual: true,
    y: sampleTerrain(terrain, poi.x, poi.z),
    slope: terrainSlope(terrain, poi.x, poi.z),
    radius: poi.radius ?? 24,
    padRadius: poi.padRadius ?? poi.radius ?? 24,
    blend: poi.blend ?? 8,
  })), ...generated];
}

function constraintContains(constraint, x, z) {
  const shape = constraint?.boundary ?? constraint?.polygon ?? constraint?.points;
  if (Array.isArray(shape)) return pointInPolygon(x, z, shape);
  if (shape?.outer) return pointInBoundary(x, z, shape);
  return false;
}

function terrainConstraint(state, x, z) {
  const regions = state.constraints?.regions ?? state.constraintRegions ?? [];
  for (let index = regions.length - 1; index >= 0; index--) {
    if (constraintContains(regions[index], x, z)) return regions[index];
  }
  return null;
}

/** Classifies a terrain point while retaining the measured slope for designers. */
export function classifyTraversal(terrain, state, x, z) {
  const slope = terrainSlope(terrain, x, z);
  if (!pointIsPlayable(state, terrain, x, z)) return { mode: "outside", slope };
  if (isWaterAt(state, terrain, x, z)) return { mode: "water", slope };
  const constraint = terrainConstraint(state, x, z);
  if (["walkable", "climbable", "blocked"].includes(constraint?.mode)) {
    if (Number.isFinite(constraint.maxSlopeDeg) && slope > constraint.maxSlopeDeg) {
      return { mode: "blocked", slope, constraintId: constraint.id ?? null, violatesConstraint: true };
    }
    return { mode: constraint.mode, slope, constraintId: constraint.id ?? null, violatesConstraint: false };
  }
  const traversal = state.traversal ?? state;
  const walkableMaxSlopeDeg = traversal.walkableMaxSlopeDeg ?? DEFAULT_WALKABLE_MAX_SLOPE_DEG;
  const climbableMaxSlopeDeg = Math.max(traversal.climbableMaxSlopeDeg ?? DEFAULT_CLIMBABLE_MAX_SLOPE_DEG, walkableMaxSlopeDeg);
  if (slope <= walkableMaxSlopeDeg) return { mode: "walkable", slope };
  if (slope <= climbableMaxSlopeDeg) return { mode: "climbable", slope };
  return { mode: "blocked", slope };
}

function readNumber(text, patterns, fallback) {
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) return Number(match[1]);
  }
  return fallback;
}

function commandAngle(text, heading = 0) {
  if (/\bnorth(?:ward)?\b/.test(text)) return -Math.PI / 2;
  if (/\bsouth(?:ward)?\b/.test(text)) return Math.PI / 2;
  if (/\beast(?:ward)?\b/.test(text)) return 0;
  if (/\bwest(?:ward)?\b/.test(text)) return Math.PI;
  return heading;
}

function nextFeatureId(state, type) {
  let index = 1;
  const ids = new Set(state.features.map((feature) => feature.id));
  while (ids.has(`${type}_${index}`)) index++;
  return `${type}_${index}`;
}

export function applyTerrainCommand(inputState, instruction, anchor = { x: 0, z: 0 }, heading = 0) {
  const text = String(instruction ?? "").trim().toLowerCase();
  if (!text) return { state: inputState, changed: false, message: "Describe a terrain change first." };
  const state = cloneTerrainState(inputState);

  if (/\b(remove|delete)\b.*\b(last|feature|rule)\b/.test(text)) {
    const removed = state.features.pop();
    return { state, changed: Boolean(removed), regeneratePois: true, message: removed ? `Removed ${removed.type.replace("_", " ")} rule.` : "There are no terrain rules to remove." };
  }
  if (/\bpoi|points? of interest|settlements?|lookouts?|camps?\b/.test(text) && !/mountain|lake|valley|ridge|river|plateau|pass/.test(text)) {
    const count = readNumber(text, [/\b(\d+)\s+(?:poi|points?|settlements?|lookouts?|camps?)/], null);
    const type = /lookout/.test(text) ? "lookout" : /lake|water/.test(text) ? "lakeside" : "settlement";
    const rule = state.poiRules.find((candidate) => candidate.type === type);
    if (count && rule) rule.count = clamp(Math.round(count), 1, 24);
    state.poiSeed = ((state.poiSeed ?? state.seed) + 7919) % 2147483647;
    return { state, changed: true, regeneratePois: true, poiOnly: true, message: `Re-applied ${type} placement rules${count ? ` for ${count} sites` : ""}.` };
  }
  if (/\b(regenerate|reroll|new seed)\b/.test(text)) {
    state.seed = (state.seed + 7919) % 2147483647;
    return { state, changed: true, regeneratePois: true, message: `Regenerated the landform seed (${state.seed}).` };
  }
  if (/\b(smoother|smooth|less rough)\b/.test(text)) {
    state.roughness = clamp(state.roughness - 0.14, 0.05, 1);
    state.erosion = clamp(state.erosion + 2, 0, 12);
    return { state, changed: true, regeneratePois: true, message: `Smoothed the base terrain; roughness is now ${state.roughness.toFixed(2)}.` };
  }
  if (/\b(rougher|more rugged|more detail)\b/.test(text)) {
    state.roughness = clamp(state.roughness + 0.14, 0.05, 1);
    state.erosion = clamp(state.erosion - 1, 0, 12);
    return { state, changed: true, regeneratePois: true, message: `Increased terrain roughness to ${state.roughness.toFixed(2)}.` };
  }
  const types = ["mountain", "ridge", "valley", "lake", "river", "plateau", "pass"];
  const type = types.find((candidate) => new RegExp(`\\b${candidate}s?\\b`).test(text));
  if (!type) {
    return { state: inputState, changed: false, message: "I can place mountains, ridges, valleys, lakes, rivers, plateaus, passes, or revise POI rules." };
  }

  const width = readNumber(text, [/(\d+(?:\.\d+)?)\s*m(?:eters?)?\s+(?:wide|across)/, /(?:radius|width)\s+(?:of\s+)?(\d+(?:\.\d+)?)\s*m/], type === "river" ? 70 : type === "ridge" || type === "valley" || type === "pass" ? 180 : 260);
  const radius = /\bradius\b/.test(text) ? width : width / 2;
  const length = readNumber(text, [/(\d+(?:\.\d+)?)\s*m(?:eters?)?\s+long/, /length\s+(?:of\s+)?(\d+(?:\.\d+)?)\s*m/], radius * (type === "river" ? 9 : 5));
  const height = readNumber(text, [/(\d+(?:\.\d+)?)\s*m(?:eters?)?\s+(?:tall|high)/, /height\s+(?:of\s+)?(\d+(?:\.\d+)?)\s*m/], type === "mountain" ? 105 : type === "ridge" ? 68 : 44);
  const depth = readNumber(text, [/(\d+(?:\.\d+)?)\s*m(?:eters?)?\s+deep/, /depth\s+(?:of\s+)?(\d+(?:\.\d+)?)\s*m/], type === "lake" ? 22 : type === "pass" ? 42 : 28);
  const feature = {
    id: nextFeatureId(state, type), type,
    x: clamp(anchor.x, -state.size * 0.48, state.size * 0.48),
    z: clamp(anchor.z, -state.size * 0.48, state.size * 0.48),
    radius: clamp(radius, 18, state.size * 0.45),
    falloff: /gentle|broad|soft/.test(text) ? 2.25 : /sharp|steep|dramatic/.test(text) ? 1.35 : 1.75,
  };
  if (["mountain", "ridge", "plateau"].includes(type)) feature.height = clamp(height, 4, 450);
  if (["lake", "valley", "river", "pass"].includes(type)) feature.depth = clamp(depth, 2, 180);
  if (["ridge", "valley", "river", "pass"].includes(type)) {
    feature.length = clamp(length, feature.radius * 2, state.size * 1.4);
    feature.angle = commandAngle(text, heading);
  }
  if (type === "lake") feature.rim = /no rim|flat shore/.test(text) ? 0 : 5;
  state.features.push(feature);
  return {
    state,
    changed: true,
    regeneratePois: true,
    feature,
    message: `Added a ${type} procedure at ${Math.round(feature.x)}, ${Math.round(feature.z)} m.`,
  };
}

export function formatFeatureSummary(feature) {
  if (feature.type === "mountain") return `${Math.round(feature.height)}m rise · ${Math.round(feature.radius * 2)}m span`;
  if (feature.type === "ridge") return `${Math.round(feature.height)}m rise · ${Math.round(feature.length)}m long`;
  if (feature.type === "plateau") return `${Math.round(feature.height)}m datum · ${Math.round(feature.radius * 2)}m span`;
  if (feature.type === "lake") return `${Math.round(feature.depth)}m basin · ${Math.round(feature.radius * 2)}m shore`;
  return `${Math.round(feature.depth)}m cut · ${Math.round(feature.length)}m long`;
}
